import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { clsx } from 'clsx'
import { ImagePlus, Loader2, RotateCcw, X } from 'lucide-react'
import { Modal, StatusIcon } from '@/components/ui'
import { INSPECTION_STATUSES, REMARK_STATUSES, type InspectionStatus } from '@/services/jobs.service'
import { MAX_JOB_IMAGES, getJobImageUrls, removeJobImages, uploadJobImage } from '@/services/jobImages.service'

const STATUS_LABELS: Record<InspectionStatus, string> = {
  OK: 'OK',
  C: 'Kommentar',
  MO: 'Memorandum',
  RC: 'Anbefaling',
  NC: 'Ikke godkjent',
  EXP: 'Utløpt',
  Missing: 'Mangler',
}

const MENU_WIDTH = 224
const MENU_GAP = 6
const VIEWPORT_MARGIN = 8

interface JobStatusPickerProps {
  assignmentId: string
  /** Status shown once the assignment is done; null while it is pending. */
  value: string | null
  remarks: string | null
  images: string[]
  done: boolean
  /** Shown above the comment field, e.g. the equipment's serial number. */
  equipmentLabel: string
  disabled?: boolean
  saving?: boolean
  onChange: (status: InspectionStatus | null, remarks: string | null, images: string[]) => void
}

const JobStatusPicker = ({
  assignmentId,
  value,
  remarks,
  images,
  done,
  equipmentLabel,
  disabled,
  saving,
  onChange,
}: JobStatusPickerProps) => {
  const [open, setOpen] = useState(false)
  const [remarkStatus, setRemarkStatus] = useState<InspectionStatus | null>(null)
  const [remarkText, setRemarkText] = useState('')
  const [remarkImages, setRemarkImages] = useState<string[]>([])
  const [newUploads, setNewUploads] = useState<string[]>([])
  const [previews, setPreviews] = useState<Record<string, string>>({})
  const [uploading, setUploading] = useState(0)
  const [imageError, setImageError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !menuRef.current) return
    const anchor = buttonRef.current.getBoundingClientRect()
    const menuHeight = menuRef.current.offsetHeight
    const fitsBelow = anchor.bottom + MENU_GAP + menuHeight <= window.innerHeight - VIEWPORT_MARGIN
    const top = fitsBelow ? anchor.bottom + MENU_GAP : Math.max(VIEWPORT_MARGIN, anchor.top - MENU_GAP - menuHeight)
    const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN))
    setPosition({ top, left })
  }, [open])

  useEffect(() => {
    if (!open) return
    const close = (event: Event) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', close)
    document.addEventListener('scroll', close, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('scroll', close, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const select = (status: InspectionStatus | null) => {
    setOpen(false)
    if (status && REMARK_STATUSES.includes(status)) {
      setRemarkText(remarks ?? '')
      setRemarkImages(images)
      setNewUploads([])
      setImageError(null)
      setRemarkStatus(status)
      getJobImageUrls(images)
        .then((urls) => setPreviews((prev) => ({ ...prev, ...urls })))
        .catch((err) => console.error('Loading job image previews failed:', err))
      return
    }
    onChange(status, null, [])
  }

  const addImages = async (files: FileList | null) => {
    if (!files?.length) return
    setImageError(null)
    const room = MAX_JOB_IMAGES - remarkImages.length
    const picked = Array.from(files).slice(0, room)
    if (files.length > room) setImageError(`Maks ${MAX_JOB_IMAGES} bilder per utstyr.`)
    setUploading((n) => n + picked.length)
    await Promise.all(
      picked.map(async (file) => {
        try {
          const path = await uploadJobImage(assignmentId, file)
          setPreviews((prev) => ({ ...prev, [path]: URL.createObjectURL(file) }))
          setRemarkImages((prev) => [...prev, path])
          setNewUploads((prev) => [...prev, path])
        } catch (err) {
          console.error('Job image upload failed:', err)
          setImageError(err instanceof Error ? err.message : 'Opplasting feilet')
        } finally {
          setUploading((n) => n - 1)
        }
      }),
    )
  }

  const removeImage = (path: string) => {
    setRemarkImages((prev) => prev.filter((p) => p !== path))
    if (newUploads.includes(path)) {
      setNewUploads((prev) => prev.filter((p) => p !== path))
      removeJobImages([path])
    }
  }

  const cancelRemark = () => {
    removeJobImages(newUploads)
    setRemarkStatus(null)
  }

  const saveRemark = () => {
    if (!remarkStatus || !remarkText.trim() || uploading > 0) return
    onChange(remarkStatus, remarkText.trim(), remarkImages)
    setRemarkStatus(null)
  }

  const label = done ? `Status: ${STATUS_LABELS[value as InspectionStatus] ?? 'utført'}` : 'Sett status'

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => {
          setPosition(null)
          setOpen((prev) => !prev)
        }}
        disabled={disabled || saving}
        className="shrink-0 w-16 flex items-center justify-center hover:bg-white transition-colors disabled:cursor-not-allowed"
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className={clsx('h-8 w-8 flex items-center justify-center', disabled && 'opacity-40')}>
          {saving ? (
            <Loader2 size={16} className="animate-spin text-slate-400" />
          ) : done && value ? (
            <StatusIcon status={value} size="lg" />
          ) : (
            <span
              className={clsx(
                'h-7 w-7 rounded-full border-2',
                done ? 'bg-emerald-500 border-emerald-500' : 'border-dashed border-slate-300',
              )}
            />
          )}
        </span>
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-50 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
            style={{
              width: MENU_WIDTH,
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            {INSPECTION_STATUSES.map((status) => (
              <button
                key={status}
                role="menuitemradio"
                aria-checked={done && value === status}
                onClick={() => select(status)}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 min-h-[44px] text-left text-sm font-medium text-slate-700 hover:bg-slate-50',
                  done && value === status && 'bg-slate-100',
                )}
              >
                <StatusIcon status={status} size="md" />
                {STATUS_LABELS[status]}
              </button>
            ))}
            {done && (
              <>
                <div className="my-1 border-t border-slate-100" />
                <button
                  role="menuitem"
                  onClick={() => select(null)}
                  className="w-full flex items-center gap-3 px-3 min-h-[44px] text-left text-sm font-medium text-slate-500 hover:bg-slate-50"
                >
                  <span className="h-6 w-6 flex items-center justify-center">
                    <RotateCcw size={16} />
                  </span>
                  Marker som ikke utført
                </button>
              </>
            )}
          </div>,
          document.body,
        )}

      <Modal
        isOpen={remarkStatus !== null}
        onClose={cancelRemark}
        title={remarkStatus ? `${STATUS_LABELS[remarkStatus]} – ${equipmentLabel}` : ''}
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {remarkStatus && <StatusIcon status={remarkStatus} size="lg" />}
            <p className="text-sm text-slate-500">Teksten og bildene legges på kontrollkortet.</p>
          </div>
          <textarea
            autoFocus
            rows={4}
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            placeholder={remarkStatus === 'C' ? 'Kommentar...' : 'Beskriv avviket...'}
            className="w-full px-4 py-3 text-base border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 focus:border-[#1E3A5F] text-slate-800"
          />
          <div>
            <div className="grid grid-cols-3 gap-2">
              {remarkImages.map((path) => (
                <div key={path} className="relative aspect-square rounded-md overflow-hidden border border-slate-200 bg-slate-100">
                  {previews[path] ? (
                    <img src={previews[path]} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <Loader2 size={18} className="animate-spin text-slate-400" />
                    </div>
                  )}
                  <button
                    onClick={() => removeImage(path)}
                    className="absolute top-1 right-1 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/75"
                    aria-label="Fjern bilde"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              {Array.from({ length: uploading }, (_, i) => (
                <div key={`uploading-${i}`} className="aspect-square rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
                  <Loader2 size={18} className="animate-spin text-slate-400" />
                </div>
              ))}
              {remarkImages.length + uploading < MAX_JOB_IMAGES && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square rounded-md border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-[#1E3A5F] hover:text-[#1E3A5F]"
                >
                  <ImagePlus size={22} />
                  <span className="text-xs font-semibold">Legg til bilde</span>
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                addImages(e.target.files)
                e.target.value = ''
              }}
            />
            {imageError && <p className="mt-2 text-sm text-red-600">{imageError}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={cancelRemark}
              className="h-12 px-5 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100"
            >
              Avbryt
            </button>
            <button
              onClick={saveRemark}
              disabled={!remarkText.trim() || uploading > 0}
              className="h-12 px-5 rounded-md text-sm font-semibold text-white bg-[#1E3A5F] hover:bg-[#16304f] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Lagre
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default JobStatusPicker
